import { Fragment } from "react"
import { Copy, Loader2, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ageFromDob, formatDateOnly } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import { closeReasonLabel, levelLabel, noteBadge, riskBadge, urgencyText, type CaseGroup } from "../lib/board"
import { statusWords } from "../lib/clock"
import type { TcmEpisode } from "../types"
import { ClockKey, ClockRuler, TcmClock } from "./TcmClock"

interface TcmCaseTableProps {
  groups: CaseGroup[]
  today: string
  ehrFor: (organizationId: string) => string
  /** Set in the cross-clinic view: the clinic shown under each patient. */
  clinicName?: (organizationId: string) => string | undefined
  onOpen: (episodeId: string) => void
  onCopy: (episodeId: string, ehr: string) => void
  copyingId: string | null
  emptyText: string
}

const HEAD = "text-[11px] font-semibold uppercase tracking-wider"

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("")
}

function stayLines(ep: TcmEpisode): [string, string] {
  const level = ep.kind === "UNKNOWN" ? "Hospital or ED: not confirmed" : levelLabel(ep.level_of_care)
  const where = ep.post_acute_facility ?? ep.facility ?? ""
  const first = where ? `${level} · ${where}` : level
  if (!ep.discharge_date) {
    return [first, ep.admit_date ? `Admitted ${formatDateOnly(ep.admit_date, "EEE M/d")}` : "Discharge date not known yet"]
  }
  const home = ep.post_acute_discharge_date ?? ep.discharge_date
  const verb = ep.post_acute_discharge_date ? "Home from SNF" : "Discharged"
  return [first, `${verb} ${formatDateOnly(home, "EEE M/d")}`]
}

function kindBadge(ep: TcmEpisode) {
  if (ep.kind === "ED_FOLLOWUP") return <StatusBadge variant="info" size="sm">ED</StatusBadge>
  if (ep.kind === "UNKNOWN") return <StatusBadge variant="warning" size="sm">Records needed</StatusBadge>
  return null
}

function PatientCell({ ep, clinic, onOpen }: { ep: TcmEpisode; clinic?: string; onOpen: () => void }) {
  const age = ageFromDob(ep.patient_dob)
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold shrink-0">
        {initials(ep.patient_name)}
      </div>
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="font-medium text-[13px] truncate text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            onClick={(e) => {
              e.stopPropagation()
              onOpen()
            }}
          >
            {ep.patient_name}
          </button>
          {kindBadge(ep)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {[ep.mrn && `MRN ${ep.mrn}`, age !== null && `${age} y`, ep.tcm_only && "not on roster"].filter(Boolean).join(" · ")}
        </div>
        {ep.patient_phone && (
          <a
            href={`tel:${ep.patient_phone}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] text-foreground/80 hover:underline"
          >
            <Phone className="h-3 w-3" />
            {ep.patient_phone}
          </a>
        )}
        {(ep.pcp || ep.insurance) && (
          <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">
            {[ep.pcp && `PCP ${ep.pcp}`, ep.insurance].filter(Boolean).join(" · ")}
          </div>
        )}
        {clinic && <span className="block text-[11px] font-medium text-foreground/80 truncate">{clinic}</span>}
      </div>
    </div>
  )
}

function StayCell({ ep }: { ep: TcmEpisode }) {
  const [stay, discharged] = stayLines(ep)
  return (
    <div className="space-y-0.5">
      <div className="text-[13px] truncate max-w-[240px]">{stay}</div>
      <div className="text-[11px] text-muted-foreground">{discharged}</div>
      {ep.f2f_visit_type && <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">Visit type: {ep.f2f_visit_type}</div>}
    </div>
  )
}

function ClockCell({ ep, today, ehr }: { ep: TcmEpisode; today: string; ehr: string }) {
  if (ep.clock) {
    return (
      <div className="space-y-1 pr-2">
        <TcmClock episode={ep} today={today} ehr={ehr} />
        <div className="text-[11px] text-muted-foreground">{statusWords(ep, today)}</div>
      </div>
    )
  }
  return (
    <span className="text-[11px] text-muted-foreground">
      {ep.kind === "ED_FOLLOWUP"
        ? "ED follow-up: no TCM clock"
        : ep.stage.code === "RECORDS_NEEDED"
          ? "Clock starts once the stay is confirmed"
          : "Starts at discharge"}
    </span>
  )
}

function NextStepCell({ ep }: { ep: TcmEpisode }) {
  const risk = riskBadge(ep)
  return (
    <div className="space-y-1">
      <div className={cn("text-[13px] font-medium", urgencyText(ep.stage.urgency))}>{ep.stage.next_action}</div>
      <div className="text-[11px] text-muted-foreground">
        {ep.stage.code === "CLOSED"
          ? closeReasonLabel(ep.closed_reason)
          : ep.stage.due_date && ep.stage.urgency !== "done"
            ? `Due ${formatDateOnly(ep.stage.due_date, "EEE M/d")}${ep.stage.detail ? ` · ${ep.stage.detail}` : ""}`
            : ep.stage.detail}
      </div>
      {risk && <StatusBadge variant={risk.variant} size="sm">{risk.label}</StatusBadge>}
    </div>
  )
}

function NoteCell({ ep, ehr, onCopy, copying }: { ep: TcmEpisode; ehr: string; onCopy: () => void; copying: boolean }) {
  const badge = ep.note_status === "held" ? { label: "Held by Pear's check", variant: "warning" as const } : noteBadge(ep, ehr)
  return (
    <div className="flex flex-col items-end gap-1.5">
      <StatusBadge variant={badge.variant} dot>{badge.label}</StatusBadge>
      {ep.note && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          disabled={copying}
          onClick={(e) => {
            e.stopPropagation()
            onCopy()
          }}
          aria-label={`Copy note for ${ep.patient_name}`}
        >
          {copying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
          Copy note
        </Button>
      )}
    </div>
  )
}

function GroupTitle({ group }: { group: CaseGroup }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
      <span className={cn("text-[13px] font-semibold", group.urgent && "text-destructive")}>
        {group.title}
        <span className="ml-1.5 font-medium text-muted-foreground">· {group.episodes.length}</span>
      </span>
      {group.hint && <span className="text-xs text-muted-foreground">{group.hint}</span>}
    </div>
  )
}

export function TcmCaseTable({ groups, today, ehrFor, clinicName, onOpen, onCopy, copyingId, emptyText }: TcmCaseTableProps) {
  if (groups.length === 0) {
    return (
      <div className="rounded-xl border bg-card px-4 py-12 text-center text-sm text-muted-foreground shadow-sm">{emptyText}</div>
    )
  }
  return (
    <>
      {/* Phones and narrow windows: one card per case, the clock at full width with its words under it. */}
      <div className="space-y-4 md:hidden">
        {groups.map((group) => (
          <section key={group.key} className="space-y-2">
            <GroupTitle group={group} />
            {group.episodes.map((ep) => {
              const ehr = ehrFor(ep.organization_id)
              return (
                <div
                  key={ep.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen(ep.id)}
                  onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onOpen(ep.id)}
                  className="space-y-3 rounded-xl border bg-card p-3 shadow-sm"
                >
                  <PatientCell ep={ep} clinic={clinicName?.(ep.organization_id)} onOpen={() => onOpen(ep.id)} />
                  <StayCell ep={ep} />
                  {ep.clock && <ClockRuler />}
                  <ClockCell ep={ep} today={today} ehr={ehr} />
                  <NextStepCell ep={ep} />
                  <NoteCell ep={ep} ehr={ehr} onCopy={() => onCopy(ep.id, ehr)} copying={copyingId === ep.id} />
                </div>
              )
            })}
          </section>
        ))}
      </div>

      <div className="hidden rounded-xl border bg-card shadow-sm md:block">
        <Table>
          <TableHeader className="sticky top-0 z-40">
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead className={cn(HEAD, "w-[22%] rounded-tl-xl")}>Patient</TableHead>
              <TableHead className={cn(HEAD, "w-[16%]")}>Stay</TableHead>
              <TableHead className={cn(HEAD, "w-[32%] min-w-[300px] py-2 align-bottom")}>
                <div className="flex items-center gap-1">
                  30-day clock
                  <ClockKey />
                </div>
                <ClockRuler className="mt-1 pr-2" />
              </TableHead>
              <TableHead className={cn(HEAD, "w-[20%]")}>Next step</TableHead>
              <TableHead className={cn(HEAD, "text-right rounded-tr-xl")}>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableCell colSpan={5} className="py-2.5">
                    <GroupTitle group={group} />
                  </TableCell>
                </TableRow>
                {group.episodes.map((ep) => {
                  const ehr = ehrFor(ep.organization_id)
                  return (
                    <TableRow
                      key={ep.id}
                      className="cursor-pointer transition-colors bg-card hover:bg-muted/50 border-b align-top"
                      onClick={() => onOpen(ep.id)}
                    >
                      <TableCell>
                        <PatientCell ep={ep} clinic={clinicName?.(ep.organization_id)} onOpen={() => onOpen(ep.id)} />
                      </TableCell>
                      <TableCell>
                        <StayCell ep={ep} />
                      </TableCell>
                      <TableCell>
                        <ClockCell ep={ep} today={today} ehr={ehr} />
                      </TableCell>
                      <TableCell>
                        <NextStepCell ep={ep} />
                      </TableCell>
                      <TableCell className="text-right">
                        <NoteCell ep={ep} ehr={ehr} onCopy={() => onCopy(ep.id, ehr)} copying={copyingId === ep.id} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  )
}
